/**
 * Frontend web-survey routes must not end in .json.
 * CloudFront's SPA rewrite skips *.json, so a full page load of
 * /web-survey/.../survey.json is treated as a missing static file (403).
 * The Control API requires a file suffix: .json, or .zip when the
 * instrument has example images / other media (has_images / has_resources).
 */

const JSON_SUFFIX = /\.json(?=$|[?#])/i
const CONTROL_FILE_EXT = /\.(json|zip)$/i

export const stripInstrumentFileExt = (path = '') =>
  String(path).replace(JSON_SUFFIX, '')

export const ensureInstrumentFileExt = (path = '') => {
  const raw = String(path)
  if (!raw) return ''
  const qIdx = raw.indexOf('?')
  const pathname = qIdx >= 0 ? raw.slice(0, qIdx) : raw
  const search = qIdx >= 0 ? raw.slice(qIdx) : ''
  if (CONTROL_FILE_EXT.test(pathname)) return `${pathname}${search}`
  return `${pathname}.json${search}`
}

export const toWebSurveyPath = (url = '') => {
  if (!url || typeof url !== 'string') return ''
  const studiesIdx = url.indexOf('/studies')
  if (studiesIdx < 0) return ''
  return stripInstrumentFileExt(`/web-survey${url.slice(studiesIdx)}`)
}

export const buildWebSurveyUrl = ({
  studyId,
  instrumentId,
  instrumentType,
  version,
  hasMedia = false,
} = {}) => {
  if (!studyId || !instrumentId || !instrumentType) return ''

  const type = String(instrumentType)
    .toLowerCase()
    .replace(/\.(json|zip)$/i, '')
  const fileName = hasMedia ? `${type}.zip` : type
  const base = `/web-survey/studies/${studyId}/instruments/${instrumentId}/${fileName}`

  const params = new URLSearchParams()
  if (version !== undefined && version !== null && String(version).length > 0) {
    params.set('version', String(version))
  }

  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

/** Same open path carousel uses: prefer catalog `url` (json vs zip), else rebuild. */
export const resolveInstrumentWebSurveyPath = (
  instrument = {},
  {
    studyId,
    instrumentId,
    instrumentType,
    version,
  } = {},
) => {
  const fromCatalog = toWebSurveyPath(instrument?.url)
  if (fromCatalog) return fromCatalog

  return buildWebSurveyUrl({
    studyId,
    instrumentId:
      instrumentId ||
      instrument?.instrument_id ||
      instrument?.identity ||
      instrument?.id,
    instrumentType:
      instrumentType || instrument?.instrument_type || instrument?.type,
    version:
      version ??
      instrument?.version ??
      instrument?.metadata?.version ??
      instrument?.metadata?.instrument_version,
    hasMedia: !!(instrument?.has_images || instrument?.has_resources),
  })
}

export const toControlSurveyUrl = (pathname = '', search = '') => {
  const studiesIdx = String(pathname).indexOf('/studies')
  const fromStudies =
    studiesIdx >= 0 ? String(pathname).slice(studiesIdx) : String(pathname)
  const withExt = ensureInstrumentFileExt(fromStudies)
  if (!withExt) return ''

  const qs =
    search && search !== '?'
      ? search.startsWith('?')
        ? search
        : `?${search}`
      : ''
  return `/control${withExt}${qs}`
}
